import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, of, take } from 'rxjs';
import { AgentAccessService, OauthConsentInfo } from '../../../services/agent-access.service';
import { AuthService } from '../../../services/auth.service';
import { isConsentRequestId, rememberPendingConsent } from './oauth-consent-pending';

type State = 'loading' | 'ask' | 'disabled' | 'gone' | 'framed' | 'leaving';

/**
 * An AI agent (Claude, Cursor, ChatGPT …) asks to act for the person —
 * al-user-management `/oauth/authorize` sends the browser here.
 *
 * Not signed in → remember the request, log in, come back (oauth-consent-pending.ts).
 * Agree → al-user-management returns the client's callback with a one-time
 * code and the browser goes there; refuse → the same with access_denied.
 * "Änderungen vorschlagen" is off unless the person switches it on — and even
 * then every change waits for their approval in the portal.
 * Docs: features/docs/technical/portal-agent-native.md "Anmeldung per OAuth".
 */
@Component({
  selector: 'app-oauth-consent',
  templateUrl: './oauth-consent.component.html',
  styleUrl: './oauth-consent.component.scss',
})
export class OauthConsentComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly access = inject(AgentAccessService);

  state: State = 'loading';
  info: OauthConsentInfo | null = null;
  canWrite = false;
  busy: 'approve' | 'deny' | null = null;
  failed = false;
  private requestId = '';

  ngOnInit(): void {
    // Agreeing is one click: never inside someone else's frame (clickjacking).
    if (window.top !== window.self) {
      this.state = 'framed';
      return;
    }
    const id = this.route.snapshot.queryParamMap.get('request');
    if (!isConsentRequestId(id)) {
      this.state = 'gone';
      return;
    }
    this.requestId = id;
    this.auth.nbAuth
      .isAuthenticatedOrRefresh()
      .pipe(
        take(1),
        catchError(() => of(false)),
      )
      .subscribe((signedIn) => {
        if (!signedIn) {
          rememberPendingConsent(id);
          this.router.navigate(['/auth/login']);
          return;
        }
        this.load();
      });
  }

  private load(): void {
    this.access.getConsent(this.requestId).subscribe({
      next: (info) => {
        this.info = info;
        this.canWrite = false;
        this.state = info.enabled ? 'ask' : 'disabled';
      },
      error: () => (this.state = 'gone'),
    });
  }

  decide(approve: boolean): void {
    if (this.busy) return;
    this.busy = approve ? 'approve' : 'deny';
    this.failed = false;
    this.access.decideConsent(this.requestId, approve, approve && this.canWrite).subscribe({
      next: ({ redirect }) => {
        this.state = 'leaving';
        window.location.href = redirect;
      },
      error: (err: HttpErrorResponse) => {
        this.busy = null;
        if (err.status === 404) this.state = 'gone';
        else if (err.status === 403) this.state = 'disabled';
        else this.failed = true;
      },
    });
  }
}
